import { WeBuildAiAdmin } from "@webuild-ai/admin";
import { ChatHistoryMetadata } from "@webuild-ai/admin/models/components";
import { create } from "zustand";

import { logger } from "@/telemetry";

/**
 * Maximum number of chat histories to fetch/display.
 * Cosmos DB RU cost is similar for larger batches, so we fetch 200 at once.
 */
const CHAT_HISTORY_BATCH_SIZE = 200;

interface ChatHistoryState {
  chatHistory: ChatHistoryMetadata[];
  displayedChats: ChatHistoryMetadata[];
  selectedChatId: string | null;
  shouldScrollToSelected: boolean; // Flag to trigger scroll to selected item (e.g., after search selection)
  offset: string | undefined; // Cursor-based pagination: ID of last chat
  hasMore: boolean;
  isLoading: boolean;
  firstLoadChatsCount: number;
  fetchChatHistory: (userId: string, adminClient: WeBuildAiAdmin) => Promise<void>;
  loadMoreChatHistory: (adminClient: WeBuildAiAdmin) => Promise<void>;
  showMoreChats: () => void;
  selectChat: (chatId: string) => boolean;
  selectAndEnsureVisible: (chatId: string, adminClient: WeBuildAiAdmin) => Promise<void>;
  clearSelection: () => void;
  clearScrollFlag: () => void; // Clear the shouldScrollToSelected flag after scrolling
  setFirstLoadChatsCount: (count: number) => void;
  deleteChatHistory: (chatHistoryId: string, adminClient: WeBuildAiAdmin) => Promise<void>;
  addNewChat: (chat: ChatHistoryMetadata) => void;
  searchChatHistory: (
    query: string,
    adminClient: WeBuildAiAdmin,
    searchInContent?: boolean,
  ) => Promise<ChatHistoryMetadata[]>;
}

/**
 * Intelligently merges local chat history with backend response
 * Preserves locally-added chats that might not be in backend yet
 * Prefers local summary if backend summary is empty/missing
 * Updates backend chats while maintaining local additions
 */
function mergeChatHistories(
  localChats: ChatHistoryMetadata[],
  backendChats: ChatHistoryMetadata[],
): ChatHistoryMetadata[] {
  // Create maps for quick lookup
  const backendMap = new Map(backendChats.map((chat) => [chat.id, chat]));
  const localMap = new Map(localChats.map((chat) => [chat.id, chat]));

  const merged: ChatHistoryMetadata[] = [];

  // Process backend chats first (they are the source of truth for existing chats)
  backendChats.forEach((backendChat) => {
    const localChat = localMap.get(backendChat.id);

    if (localChat) {
      // Chat exists in both - intelligently merge
      // Prefer local summary if backend summary is empty/missing
      if ((!backendChat.summary || backendChat.summary.trim() === "") && localChat.summary) {
        logger.info("📌 Preserving local summary for chat", {
          chatId: String(backendChat.id),
          localSummary: String(localChat.summary),
          backendSummary: String(backendChat.summary),
        });
        merged.push({
          ...backendChat,
          summary: localChat.summary, // Use local summary
        });
      } else {
        // Backend has valid summary - use it
        merged.push(backendChat);
      }
    } else {
      // Chat only in backend - use backend version
      merged.push(backendChat);
    }
  });

  // Add local chats that aren't in backend at all (recently created, not yet synced)
  localChats.forEach((localChat) => {
    if (!backendMap.has(localChat.id)) {
      // Local chat not in backend - keep it (recently created)
      merged.push(localChat);
      logger.info("📌 Preserving local chat not in backend", {
        chatId: String(localChat.id),
        summary: String(localChat.summary),
      });
    }
  });

  // Sort by timestamp (newest first)
  merged.sort((a, b) => {
    const timeA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const timeB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;

    return timeB - timeA;
  });

  return merged;
}

export const useChatHistoryStore = create<ChatHistoryState>((set, get) => ({
  chatHistory: [],
  displayedChats: [],
  selectedChatId: null,
  shouldScrollToSelected: false,
  offset: undefined, // Initial state: no cursor yet
  hasMore: true,
  isLoading: false,
  firstLoadChatsCount: CHAT_HISTORY_BATCH_SIZE,
  clearScrollFlag: () => set({ shouldScrollToSelected: false }),
  deleteChatHistory: async (chatHistoryId: string, adminClient: WeBuildAiAdmin) => {
    await adminClient.chatHistories.delete({ id: chatHistoryId });
    const filterFn = (chat: ChatHistoryMetadata) => chat.id !== chatHistoryId;
    const state = get();

    set({
      chatHistory: state.chatHistory.filter(filterFn),
      displayedChats: state.displayedChats.filter(filterFn),
      selectedChatId: state.selectedChatId === chatHistoryId ? null : state.selectedChatId,
    });
  },
  fetchChatHistory: async (userId, adminClient) => {
    logger.info("fetchChatHistory", { userId });

    // Use a promise wrapper to safely handle state updates
    return new Promise<void>((resolve, reject) => {
      // Check if we can safely update state
      let canUpdate = true;

      const safeSet = (updates: any) => {
        if (canUpdate) {
          try {
            set(updates);
          } catch (error) {
            logger.warn("⚠️ ChatHistoryStore state update failed (component may be unmounted)", {
              error: String(error),
            });
            canUpdate = false;
          }
        }
      };

      safeSet({ isLoading: true });

      adminClient.chatHistories
        .listChatHistories({
          limit: CHAT_HISTORY_BATCH_SIZE,
          orderby: "updated_at",
          order: "desc",
        })
        .then((response) => {
          if (!canUpdate) {
            resolve();

            return;
          }

          const chatHistories = (Array.isArray(response) ? response : []) as ChatHistoryMetadata[];

          // Debug log to see chat history structure
          logger.info("🔍 ChatHistoryStore: Received chat histories from API", {
            count: String(chatHistories.length),
          });

          // Merge backend response with local state to preserve recently-created chats
          const currentChatHistory = get().chatHistory;
          const mergedChatHistory = mergeChatHistories(currentChatHistory, chatHistories);

          logger.info(
            `🔄 Merged chat histories: ${mergedChatHistory.length} total (backend: ${chatHistories.length}, local: ${currentChatHistory.length})`,
          );

          // Show only first N chats initially
          const initialDisplayCount = get().firstLoadChatsCount;
          const displayedChats = mergedChatHistory.slice(0, initialDisplayCount);

          // hasMore is true if we got exactly CHAT_HISTORY_BATCH_SIZE chats (means there might be more on server)
          // OR if we have more locally to display
          const hasMoreOnServer = chatHistories.length === CHAT_HISTORY_BATCH_SIZE;
          const hasMoreLocally = mergedChatHistory.length > initialDisplayCount;
          const hasMoreChats = hasMoreOnServer || hasMoreLocally;

          // Set offset to the ID of the last chat for cursor-based pagination
          const lastChatId = chatHistories[chatHistories.length - 1]?.id;

          // Preserve existing selection if it exists (for URL deep linking)
          // BUT if selection was explicitly cleared (null), keep it cleared
          const currentSelection = get().selectedChatId;

          safeSet({
            chatHistory: mergedChatHistory,
            displayedChats: displayedChats,
            offset: lastChatId, // Use last chat ID for cursor-based pagination
            selectedChatId: currentSelection, // Preserve selection for URL deep linking, or null if cleared
            hasMore: hasMoreChats,
            isLoading: false,
          });
          resolve();
        })
        .catch((error) => {
          logger.error("Error fetching chat history", error);
          if (canUpdate) {
            safeSet({ isLoading: false });
          }
          reject(error);
        });

      // Set a timeout to prevent hanging promises and disable updates after a reasonable time
      setTimeout(() => {
        canUpdate = false;
      }, 10000);
    });
  },

  loadMoreChatHistory: async (adminClient) => {
    const { offset, isLoading, hasMore, chatHistory } = get();

    if (isLoading || !hasMore) {
      logger.info("⏸️ Skipping loadMore", {
        isLoading: String(isLoading),
        hasMore: String(hasMore),
      });

      return;
    }

    set({ isLoading: true });

    try {
      logger.info("📥 Loading more chats", { offset: String(offset) });

      const response = await adminClient.chatHistories.listChatHistories({
        limit: CHAT_HISTORY_BATCH_SIZE,
        offset: offset,
        orderby: "updated_at",
        order: "desc",
      });

      const newChats = (Array.isArray(response) ? response : []) as ChatHistoryMetadata[];

      logger.info(`✅ Loaded ${newChats.length} more chats`, {
        firstChatId: String(newChats[0]?.id),
        lastChatId: String(newChats[newChats.length - 1]?.id),
      });

      // Remove duplicates by ID before appending (safety check)
      const existingIds = new Set(chatHistory.map((chat) => chat.id));
      const uniqueNewChats = newChats.filter((chat) => !existingIds.has(chat.id));

      // Append only unique new chats to existing history
      const updatedHistory = [...chatHistory, ...uniqueNewChats];

      // Set offset to the ID of the last chat for cursor-based pagination
      const newLastChatId = updatedHistory[updatedHistory.length - 1]?.id;

      // If we got fewer chats than requested, there are no more chats to load
      const hasMoreChats = newChats.length === CHAT_HISTORY_BATCH_SIZE;

      // Preserve existing selection when loading more
      const currentSelection = get().selectedChatId;

      set({
        chatHistory: updatedHistory,
        displayedChats: updatedHistory, // Show all loaded chats
        offset: newLastChatId, // Use last chat ID for cursor-based pagination
        selectedChatId: currentSelection, // Preserve selection when loading more
        hasMore: hasMoreChats,
        isLoading: false,
      });
    } catch (error) {
      logger.error("Error loading more chat history", error);
      set({ isLoading: false });
    }
  },

  showMoreChats: () => {
    const { chatHistory, displayedChats } = get();

    // If we have more chats locally that aren't displayed yet, show them
    if (displayedChats.length < chatHistory.length) {
      const nextDisplayCount = Math.min(displayedChats.length + 10, chatHistory.length);
      const newDisplayedChats = chatHistory.slice(0, nextDisplayCount);

      set({
        displayedChats: newDisplayedChats,
        hasMore: newDisplayedChats.length < chatHistory.length || get().hasMore,
      });
    }
  },

  selectChat: (chatId: string) => {
    const { chatHistory, displayedChats } = get();

    // Check if chat exists in displayed chats
    const chatInDisplayed = displayedChats.some((chat) => chat.id === chatId);
    const chatInHistory = chatHistory.find((chat) => chat.id === chatId);

    if (!chatInDisplayed && chatInHistory) {
      // Chat exists in full history but not in displayed chats - add it
      logger.info("📌 ChatHistoryStore: Adding selected chat to displayed list", { chatId });
      set({
        selectedChatId: chatId,
        displayedChats: [...displayedChats, chatInHistory],
      });
    } else if (!chatInDisplayed) {
      logger.info(
        "📌 ChatHistoryStore: Selecting chat not yet in history list (URL deep link or old chat)",
        { chatId },
      );
      // Set selected chat regardless - it will be scrolled to when loaded
      set({ selectedChatId: chatId });
    } else {
      // Chat already in displayed list - just select it
      set({ selectedChatId: chatId });
    }

    return true; // Always return success
  },

  selectAndEnsureVisible: async (chatId: string, adminClient: WeBuildAiAdmin) => {
    const { displayedChats, chatHistory } = get();

    // Check if chat is already in displayed list
    const chatInDisplayed = displayedChats.some((chat) => chat.id === chatId);

    if (chatInDisplayed) {
      // Already visible, just select it and trigger scroll
      set({ selectedChatId: chatId, shouldScrollToSelected: true });

      return;
    }

    // Check if chat is in full history but not displayed
    const chatInHistory = chatHistory.find((chat) => chat.id === chatId);

    if (chatInHistory) {
      // Add to displayed list and select, trigger scroll to show it
      set({
        selectedChatId: chatId,
        displayedChats: [...displayedChats, chatInHistory],
        shouldScrollToSelected: true,
      });

      return;
    }

    // Chat not in local data - fetch it from the API
    try {
      const chatData = await adminClient.chatHistories.get({ id: chatId });

      if (chatData) {
        // Add the fetched chat to both history and displayed lists
        // Map ChatHistory to ChatHistoryMetadata (they have different fields)
        const chatMetadata: ChatHistoryMetadata = {
          id: chatData.id,
          tenant: chatData.tenant,
          username: chatData.username,
          summary: chatData.summary || "Chat",
          archived: chatData.archived,
          metrics: chatData.metrics,
          fileLocation: "", // Not available from .get() endpoint
          createdAt: chatData.timestamp, // Use timestamp as creation time
          updatedAt: chatData.timestamp, // Use timestamp as update time
          messageCount: chatData.messages?.length || 0,
          status: "active",
        };

        set({
          selectedChatId: chatId,
          chatHistory: [...chatHistory, chatMetadata],
          displayedChats: [...displayedChats, chatMetadata],
          shouldScrollToSelected: true,
        });
      }
    } catch (error) {
      logger.error("Failed to fetch chat for sidebar", error);
      // Still select it even if fetch fails - the main panel will show the content
      set({ selectedChatId: chatId, shouldScrollToSelected: true });
    }
  },

  setFirstLoadChatsCount: (count: number) => {
    set({ firstLoadChatsCount: count });
  },

  clearSelection: () => {
    set({ selectedChatId: null });
  },

  addNewChat: (chat: ChatHistoryMetadata) => {
    const current = get().chatHistory;
    const currentDisplayed = get().displayedChats;

    // Add new chat to the beginning of both lists
    set({
      chatHistory: [chat, ...current],
      displayedChats: [chat, ...currentDisplayed],
    });
  },

  searchChatHistory: async (
    query: string,
    adminClient: WeBuildAiAdmin,
    searchInContent: boolean = true,
  ) => {
    try {
      const results = await adminClient.chatHistories.searchChatHistories({
        query,
        limit: 100,
        searchInContent,
      });

      return results;
    } catch (error) {
      logger.error("Error searching chat history", error);

      return [];
    }
  },
}));
